/**
 * Classes partagées du design system Exidis — servies par `@exidis/ui`.
 *
 * Ce module ne DÉFINIT plus le bloc commun : il le ré-exporte. C'est ce qui garantit qu'il est
 * identique à l'octet dans tous les produits du studio — plus rien à recopier, plus rien à
 * faire diverger. Toute la variation produit passe par les tokens CSS (`index.css`), jamais
 * par ces classes.
 *
 * Les variantes propres à exiPlan se déclarent SOUS le ré-export, et seulement si le
 * référentiel n'a pas déjà l'équivalent. Une variante qui finit par servir dans un second
 * produit remonte dans `@exidis/ui` — elle ne se recopie pas.
 */

export * from '@exidis/ui';

/* ───────────────────────── variantes propres à exiPlan ───────────────────────── */

/** Cellule de la grammaire compartimentée : le contenu vit dans des cellules, pas des cartes. */
export const PANEL = 'relative border-2 border-line bg-paper';

/**
 * `PAGE_TITLE` sans `truncate` — `PageHeader` (common.jsx) l'emploie quand `title` est un
 * composant interactif (sélecteur d'organisation/équipe) plutôt qu'une chaîne : `truncate` pose
 * `overflow: hidden` sur le `<h1>`, qui rognait alors le menu déroulant (position absolue,
 * descendant du `<h1>`) au lieu de simplement tronquer un texte trop long.
 */
export const PAGE_TITLE_INTERACTIVE = 'mt-1 text-2xl font-semibold tracking-title text-ink';

/**
 * Déclencheur de sélecteur en-tête de page (217:4531 : organisation, équipe) — le titre suivi
 * d'un chevron, SANS cadre. Même grammaire que `LienNav`/`DD_TRIGGER` (aplat au survol, aucune
 * bordure visible tant qu'il n'est pas survolé) mais au gabarit typographique de `PAGE_TITLE`,
 * que `LienNav` ne porte pas (ses classes figent une petite capitale mono de barre de
 * navigation, pas un titre de page) : composé ici plutôt qu'avec le composant `LienNav` pour
 * ne pas faire concurrencer deux tailles de police sur le même élément.
 *
 * Revu le 2026-09-27 (relecture des captures) : SEUL le chevron signale qu'il ouvre un menu —
 * le titre reste `text-ink` au repos ET au survol, l'aplat `line-soft` est l'unique retour de
 * survol (même grammaire que `NAV_LINK_IDLE`, jamais une couleur de texte à l'accent).
 */
export const PAGE_TITLE_TRIGGER =
  'inline-flex items-center gap-1.5 border-2 border-transparent text-ink transition-colors hover:bg-line-soft';

/**
 * Grille de colonnes partagée par les tableaux de membres (organisation, équipe) — 217:4531 :
 * « [FILL | 210 | 150 | 230] ». La première colonne (identité) reste `w-auto` dans un
 * `<table className="table-fixed">` : ce sont CES TROIS colonnes qui doivent rester identiques
 * d'un tableau à l'autre, Rôle en tête (largeur fixe et alignée, exigée par la spec).
 */
export const COL_ROLE = 'w-[210px]';
export const COL_STATUS = 'w-[150px]';
export const COL_ACTIONS = 'w-[230px]';
